import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  calcTableHeight,
  calcTableWidth,
  copyToClipboard,
  createOnTableChangeHandler,
  downloadFileFromURL,
  formatFilter,
} from '../../../utils/utilFunctions';
import { Button, Dropdown, Menu, Table } from 'fave-ui';
import { TableSettingsType } from '../../../types/configTypes';
import { defaultTableConfig } from '../../../helpers/defaults';
import { Funnel } from 'phosphor-react';
import {
  PaymentMode,
  useFetchPaymentModeList,
} from '../../../services/MySubmissions';
import { paymentModeStatusFilter } from '../helpers';
import { MySubmissionsStatusTag } from '../../../components/common/StatusTag';
import { PaymentModeStatusWithPopover } from '../helpers/SubmissionStatus';
import { MySubmissionStatusType } from '../../../types/dataTypes';
import { mySubmissionElementIDs } from '../elementId';

// to make the table height within the viewport
const tableHeight = calcTableHeight(400);
const tableWidth = calcTableWidth();

export const PaymentModeTab: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [tableConfig, setTableConfig] =
    useState<TableSettingsType>(defaultTableConfig);

  const { data: paymentModeData, isFetching: isFetchingPaymentModeData } =
    useFetchPaymentModeList({
      page: tableConfig.current,
      filter: `partner=${searchParams.get('query') || ''}|${
        tableConfig.filter || ''
      }`,
    });

  const tableChangeHandler = createOnTableChangeHandler(
    setTableConfig,
    filters =>
      formatFilter([
        { key: 'mc_my_submission_status', value: filters.submission_status },
      ]),
  );

  const paymentModeColumns: ColumnsType<PaymentMode> = [
    {
      title: 'Submission Date',
      dataIndex: 'submission_time',
      width: 200,
    },
    {
      title: 'Submitted by',
      dataIndex: 'submitted_by',
      width: 200,
    },
    {
      title: 'Merchant Name',
      dataIndex: 'merchant_name',
      width: 200,
    },
    {
      title: 'Outlet Name',
      dataIndex: 'outlet_name',
      width: 200,
    },
    {
      title: 'Payment Mode',
      dataIndex: 'payment_mode_count',
      width: 200,
    },
    {
      title: 'Submission Status',
      dataIndex: 'submission_status',
      width: 200,
      filterIcon: (
        <Funnel
          size={16}
          weight="light"
          data-id={mySubmissionElementIDs.paymentModeSubmissionStatusFilterIcon}
        />
      ),
      filters: paymentModeStatusFilter,
      filterMode: 'tree',
      render: (status: MySubmissionStatusType, data: PaymentMode) => {
        const shouldRenderStatusTagComponent =
          status !== 'Pending Operations' &&
          status !== 'Contract Signed' &&
          status !== 'Signed';

        return shouldRenderStatusTagComponent ? (
          <MySubmissionsStatusTag status={status} />
        ) : (
          <PaymentModeStatusWithPopover paymentMode={data} />
        );
      },
    },
    {
      title: 'Actions',
      key: 'action',
      width: 120,
      render: (_, data) => <PaymentModeActionItems data={data} />,
      fixed: 'right',
    },
  ];

  return (
    <Table
      dataSource={paymentModeData?.payment_modes}
      columns={paymentModeColumns}
      scroll={{
        x: tableWidth,
        y: tableHeight,
      }}
      onChange={tableChangeHandler}
      loading={isFetchingPaymentModeData}
      pagination={{
        total: paymentModeData?.count,
        position: ['bottomCenter'],
        pageSize: tableConfig.pageSize,
        current: tableConfig.page,
        showSizeChanger: false,
      }}
      rowKey={row => row.id}
    />
  );
};

const PaymentModeActionItems = ({ data }: { data: PaymentMode }) => {
  const navigate = useNavigate();

  const items = [
    {
      label: (
        <span data-id={mySubmissionElementIDs.paymentModeViewBtn}>View</span>
      ),
      onClick: () =>
        navigate(
          `/admin/merchant_connect/my_submissions/payment_modes/${data.id}`,
        ),
    },
  ];

  switch (data.submission_status) {
    case 'Pending Ack.':
      if (data.addendum_link) {
        const addendumLink = data.addendum_link;
        items.push({
          label: (
            <span data-id={mySubmissionElementIDs.paymentModeCopyLinkBtn}>
              Copy Link
            </span>
          ),
          onClick: () => copyToClipboard(addendumLink),
        });
      }
      break;
    case 'Signed':
    case 'Pending Operations':
    case 'Completed':
    case 'Cancelled':
      if (data.contract_url) {
        const contractUrl = data.contract_url;
        items.push({
          label: (
            <span
              data-id={mySubmissionElementIDs.paymentModeDownloadContractBtn}
            >
              Download Contract
            </span>
          ),
          onClick: () => downloadFileFromURL(contractUrl),
        });
      }
      break;
  }

  const menuItems = items.map((item, index) => ({ ...item, key: index }));
  const menu = <Menu items={menuItems} />;

  return (
    <Dropdown overlay={menu} trigger={['hover']} placement={'bottomLeft'}>
      <Button
        type={'text'}
        data-id={mySubmissionElementIDs.paymentModeActionBtn}
        onClick={e => e.preventDefault()}
      >
        {'. . .'}
      </Button>
    </Dropdown>
  );
};
